import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query';
import type { FilterOption } from '@/components/shared';
import type { IndustryHierarchy } from './industry-sync';

/**
 * Resolve the canonical option list a brief-applied `industry` / `subIndustry`
 * value should match against. Industry parents + sub-industry children come from
 * the one-shot taxonomy tree (cached under `industry.tree()`), not the flat
 * /audience/lookups cache (NPJB-334).
 *
 * Three return values:
 * - `null`  — not an industry key; the caller falls through to its normal
 *   /audience/lookups (or geo) cache lookup.
 * - `FilterOption[]` — resolve the brief labels against these canonical options.
 * - `[]`    — it *is* an industry key but the hierarchy tree isn't cached yet,
 *   so there's no canonical match this run; labels fall back to their raw-string
 *   selections.
 *
 * Callers must combine the result with `??` (not `||`) so the empty-array case
 * is preserved and not collapsed into the generic-cache fallback.
 */
export function industryHierarchyOptions(
  configKey: string,
  queryClient: QueryClient,
): FilterOption[] | null {
  if (configKey !== 'industry' && configKey !== 'subIndustry') return null;
  const hierarchy = queryClient.getQueryData<IndustryHierarchy>(queryKeys.industry.tree());
  if (!hierarchy) return [];
  return configKey === 'industry' ? hierarchy.parentOptions : hierarchy.childOptions;
}
